// BoardPage — 사내 게시판 (공지사항과 같은 BBS 테이블 템플릿)
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../../shared/api/client";
import { PageHeader } from "../../shared/components/PageHeader";
import { I } from "../../shared/Icon";
import { toast } from "../../shared/stores/toasts";

// ─── Types ─────────────────────────────────────────────────────────────────

export type BoardCategory = {
  code: string; label: string; color: string; order: number;
};

export type Post = {
  id: number; category: string; title: string; body_md: string;
  author_id: number | null; author_name: string;
  is_anonymous: boolean; is_pinned: boolean;
  views: number; like_count: number; comment_count: number;
  tags: string[]; created_at: string; updated_at: string;
};
export type Comment = {
  id: number; post_id: number; parent_id: number | null;
  author_id: number | null; author_name: string; body_md: string;
  is_anonymous: boolean; deleted: boolean; created_at: string; replies: Comment[];
};
export type PostDetail = Post & {
  comments: Comment[];
  poll: { options: { text: string; votes: number }[]; ends_at: string | null } | null;
};

// ─── Helpers ───────────────────────────────────────────────────────────────

const COLOR_MAP: Record<string, string> = {
  ok: "#22c55e", warn: "#f59e0b", info: "#6366f1", muted: "#94a3b8",
  danger: "#ef4444",
};
const CAT_FALLBACK: Record<string, string> = {
  FREE: "#6366f1", PRAISE: "#22c55e", QA: "#f59e0b",
  CLUB: "#3b82f6", MARKET: "#94a3b8", ANON: "#6b7280",
};
const AVATAR_COLORS = [
  "#6366f1","#8b5cf6","#ec4899","#f43f5e",
  "#f97316","#eab308","#22c55e","#14b8a6","#3b82f6",
];

export function catColor(cats: BoardCategory[], code: string): string {
  const c = cats.find(x => x.code === code);
  return c ? (COLOR_MAP[c.color] || "#6366f1") : (CAT_FALLBACK[code] || "#6366f1");
}
function avatarColor(id: number | null) { return AVATAR_COLORS[(id || 0) % AVATAR_COLORS.length]; }

export function fmtDate(iso: string) {
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "방금";
  if (diffMin < 60) return `${diffMin}분 전`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}시간 전`;
  return `${String(d.getFullYear()).slice(2)}.${String(d.getMonth()+1).padStart(2,"0")}.${String(d.getDate()).padStart(2,"0")}`;
}

// ─── Shared BBS Tabs ────────────────────────────────────────────────────────

function BbsTabs({ active }: { active: "notice" | "board" }) {
  const nav = useNavigate();
  const tabs = [
    { key: "notice", label: "📌 공지사항", path: "/notice" },
    { key: "board",  label: "💬 게시판",   path: "/board"  },
  ] as const;
  return (
    <div style={{
      display: "flex", gap: 2, borderBottom: "2px solid var(--border)",
      marginBottom: 14,
    }}>
      {tabs.map(t => (
        <button key={t.key} onClick={() => nav(t.path)} style={{
          padding: "8px 18px", fontSize: 13, fontWeight: active === t.key ? 700 : 400,
          color: active === t.key ? "var(--p-500, #6366f1)" : "var(--text-3)",
          background: "none", border: "none", cursor: "pointer",
          borderBottom: active === t.key ? "2px solid var(--p-500, #6366f1)" : "2px solid transparent",
          marginBottom: -2, borderRadius: "6px 6px 0 0",
          transition: "all 0.15s",
        }}>{t.label}</button>
      ))}
    </div>
  );
}

// ─── Category Filter Bar ───────────────────────────────────────────────────

function CatFilterBar({ cats, active, onSelect }: {
  cats: BoardCategory[]; active: string; onSelect: (code: string) => void;
}) {
  return (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", padding: "8px 0 12px" }}>
      <button onClick={() => onSelect("")} style={{
        padding: "4px 12px", borderRadius: 20, fontSize: 12, border: "none", cursor: "pointer",
        background: !active ? "var(--p-500, #6366f1)" : "var(--panel)",
        color: !active ? "#fff" : "var(--text-2)", fontWeight: !active ? 700 : 400,
      }}>전체</button>
      {cats.map(c => {
        const col = catColor(cats, c.code);
        const isActive = active === c.code;
        return (
          <button key={c.code} onClick={() => onSelect(c.code)} style={{
            padding: "4px 12px", borderRadius: 20, fontSize: 12, border: "none", cursor: "pointer",
            background: isActive ? col : "var(--panel)",
            color: isActive ? "#fff" : "var(--text-2)", fontWeight: isActive ? 700 : 400,
          }}>{c.label}</button>
        );
      })}
    </div>
  );
}

// ─── Avatar ─────────────────────────────────────────────────────────────────

export function Avatar({ name, id, size = 26 }: { name: string; id: number | null; size?: number }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%", flexShrink: 0,
      background: avatarColor(id), color: "#fff",
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: size * 0.42, fontWeight: 700,
    }}>
      {(name || "?").slice(0, 1).toUpperCase()}
    </div>
  );
}

// ─── BBS Table Row ──────────────────────────────────────────────────────────

function PostRow({
  post, cats, active, onOpen,
}: {
  post: Post; cats: BoardCategory[];
  active: boolean; onOpen: (id: number) => void;
}) {
  const [hov, setHov] = useState(false);
  const col = catColor(cats, post.category);
  const catObj = cats.find(c => c.code === post.category);
  const accent = post.is_pinned ? "#ef4444" : col;

  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      onClick={() => onOpen(post.id)}
      style={{
        display: "grid",
        gridTemplateColumns: "104px 1fr 90px 76px 40px 52px",
        alignItems: "center",
        padding: "0 20px",
        height: 50,
        cursor: "pointer",
        borderBottom: "1px solid var(--border)",
        background: active
          ? `${col}0d`
          : hov
          ? "rgba(99,102,241,0.05)"
          : post.is_pinned
          ? "rgba(239,68,68,0.04)"
          : "transparent",
        transition: "background 0.1s",
      }}
    >
      {/* Category */}
      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        {post.is_pinned && <span style={{ fontSize: 11 }}>📌</span>}
        <span style={{
          display: "inline-block", padding: "2px 8px", borderRadius: 20,
          fontSize: 11, fontWeight: 600,
          background: col + "22", color: col,
        }}>
          {catObj?.label ?? post.category}
        </span>
      </div>

      {/* Title */}
      <div style={{
        fontSize: 14, fontWeight: post.is_pinned ? 600 : 400,
        overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis",
        paddingLeft: 10, marginLeft: 4,
        borderLeft: `3px solid ${active ? col : hov ? col + "88" : accent + "44"}`,
        paddingRight: 12, color: "var(--text)",
        transition: "border-color 0.12s",
      }}>
        {post.title}
        {post.like_count >= 10 && <span style={{ marginLeft: 6, fontSize: 12 }}>🔥</span>}
      </div>

      {/* Author */}
      <div style={{ display: "flex", alignItems: "center", gap: 5, overflow: "hidden" }}>
        <Avatar name={post.author_name} id={post.author_id} size={20} />
        <span style={{ fontSize: 12, color: "var(--text-3)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {post.author_name}
        </span>
      </div>

      {/* Date */}
      <div style={{ fontSize: 11, color: "var(--text-3)", textAlign: "center" }}>
        {fmtDate(post.created_at)}
      </div>

      {/* Comments */}
      <div style={{ fontSize: 11, color: "var(--text-3)", textAlign: "center" }}>
        💬{post.comment_count}
      </div>

      {/* Views */}
      <div style={{ fontSize: 11, color: "var(--text-3)", textAlign: "right" }}>
        {post.views}
      </div>
    </div>
  );
}

// ─── Comment Item ───────────────────────────────────────────────────────────

export function CommentItem({ comment, me, depth, onReply, onDelete }: {
  comment: Comment; me: number | null; depth: number;
  onReply: (id: number) => void; onDelete: (id: number) => void;
}) {
  return (
    <div style={{ marginLeft: depth * 20 }}>
      <div style={{
        padding: "8px 10px", borderRadius: 8, marginBottom: 4,
        background: depth === 0 ? "var(--panel, #f8fafc)" : "var(--bg-1, #f1f5f9)",
      }}>
        <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 4 }}>
          <Avatar name={comment.author_name} id={comment.author_id} size={20} />
          <span style={{ fontSize: 12, fontWeight: 600 }}>{comment.author_name}</span>
          <span style={{ fontSize: 11, color: "var(--text-3)" }}>{fmtDate(comment.created_at)}</span>
          <div style={{ marginLeft: "auto", display: "flex", gap: 4 }}>
            {depth === 0 && !comment.deleted && (
              <button className="t-btn" style={{ fontSize: 10, padding: "1px 6px" }}
                onClick={() => onReply(comment.id)}>↩ 답글</button>
            )}
            {me === comment.author_id && !comment.deleted && (
              <button className="t-btn" style={{ fontSize: 10, padding: "1px 6px", color: "#ef4444" }}
                onClick={() => onDelete(comment.id)}>삭제</button>
            )}
          </div>
        </div>
        <div style={{ fontSize: 13, whiteSpace: "pre-wrap", lineHeight: 1.6, color: comment.deleted ? "var(--text-3)" : "var(--text-1)" }}>
          {comment.body_md}
        </div>
      </div>
      {comment.replies.map(r => (
        <CommentItem key={r.id} comment={r} me={me} depth={depth+1} onReply={onReply} onDelete={onDelete} />
      ))}
    </div>
  );
}

// ─── Write / Edit Modal ─────────────────────────────────────────────────────

export function PostModal({ cats, initial, onClose, onSave }: {
  cats: BoardCategory[]; initial?: Partial<Post>; onClose: () => void; onSave: () => void;
}) {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState({
    category: initial?.category ?? (cats[0]?.code ?? "FREE"),
    title: initial?.title ?? "",
    body_md: initial?.body_md ?? "",
    is_anonymous: initial?.is_anonymous ?? false,
    tags: initial?.tags?.join(", ") ?? "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      const tagList = form.tags.split(",").map(t => t.trim()).filter(Boolean);
      const payload = { category: form.category, title: form.title.trim(), body_md: form.body_md, is_anonymous: form.is_anonymous, tags: tagList };
      if (isEdit) {
        await api.patch(`/forum/posts/${initial!.id}`, payload);
        toast.success("게시글 수정됨");
      } else {
        await api.post("/forum/posts", payload);
        toast.success("게시글 등록됨");
      }
      onSave(); onClose();
    } catch (e: any) {
      toast.error(isEdit ? "수정 실패" : "등록 실패", e?.response?.data?.error?.message ?? "");
    } finally { setSaving(false); }
  };

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
      display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, padding: 16,
    }} onClick={onClose}>
      <div className="t-card" style={{ width: "min(600px, 100%)", maxHeight: "90vh", overflow: "auto", padding: 24, gap: 16, display: "grid" }}
        onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <h3 style={{ margin: 0, fontSize: 15 }}>{isEdit ? "게시글 수정" : "새 게시글"}</h3>
          <button className="t-btn" onClick={onClose}><I.X size={14}/></button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 8, alignItems: "end" }}>
          <div>
            <label className="t-label">카테고리</label>
            <select className="t-input" value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
              {cats.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
            </select>
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: 6, paddingBottom: 6, cursor: "pointer", fontSize: 13 }}>
            <input type="checkbox" checked={form.is_anonymous} onChange={e => setForm(f => ({ ...f, is_anonymous: e.target.checked }))} />
            익명
          </label>
        </div>
        <div>
          <label className="t-label">제목</label>
          <input className="t-input" value={form.title} placeholder="제목을 입력하세요" onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
        </div>
        <div>
          <label className="t-label">내용</label>
          <textarea className="t-input" rows={8} style={{ resize: "vertical" }} value={form.body_md} placeholder="내용을 입력하세요..." onChange={e => setForm(f => ({ ...f, body_md: e.target.value }))} />
        </div>
        <div>
          <label className="t-label">태그 <span style={{ fontWeight: 400, color: "var(--text-3)" }}>(쉼표로 구분)</span></label>
          <input className="t-input" value={form.tags} placeholder="기획, 업무, 공지" onChange={e => setForm(f => ({ ...f, tags: e.target.value }))} />
        </div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button className="t-btn" onClick={onClose}>취소</button>
          <button className="t-btn t-btn-primary" onClick={submit} disabled={!form.title.trim() || saving}>
            {saving ? "저장 중…" : isEdit ? "수정" : "등록"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ──────────────────────────────────────────────────────────────

const PAGE_SIZE = 20;

export default function BoardPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const filterParam = searchParams.get("filter") || "";
  const SPECIAL = ["myposts", "mycomments"];
  const category = SPECIAL.includes(filterParam) ? "" : filterParam;
  const isMyPosts = filterParam === "myposts";

  const navigate = useNavigate();
  const [cats,     setCats]     = useState<BoardCategory[]>([]);
  const [posts,    setPosts]    = useState<Post[] | null>(null);
  const [page,     setPage]     = useState(1);
  const [search,   setSearch]   = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [showWrite, setShowWrite] = useState(false);

  const loadCats = useCallback(() => {
    api.get<BoardCategory[]>("/forum/categories").then(r => setCats(r.data)).catch(() => setCats([]));
  }, []);

  useEffect(() => {
    loadCats();
    const h = () => loadCats();
    window.addEventListener("forum-categories-updated", h);
    return () => window.removeEventListener("forum-categories-updated", h);
  }, [loadCats]);

  const loadPosts = useCallback(() => {
    setPosts(null);
    const p = new URLSearchParams();
    if (category) p.set("category", category);
    if (isMyPosts) p.set("my", "true");
    if (search.trim()) p.set("q", search.trim());
    p.set("page", String(page));
    p.set("size", String(PAGE_SIZE));
    api.get<Post[]>(`/forum/posts?${p}`).then(r => setPosts(r.data)).catch(() => setPosts([]));
  }, [category, isMyPosts, page, search]);

  useEffect(() => { setPage(1); setSearch(""); setSearchInput(""); }, [filterParam]);
  useEffect(() => { loadPosts(); }, [loadPosts]);

  const handleSelectCat = (code: string) => {
    setSearchParams(p => { const n = new URLSearchParams(p); if (code) n.set("filter", code); else n.delete("filter"); return n; });
  };

  const openPost = (id: number) => navigate(`/board/${id}`);

  const pinned = posts?.filter(p => p.is_pinned) ?? [];
  const normal = posts?.filter(p => !p.is_pinned) ?? [];

  return (
    <div style={{ maxWidth: 1280, margin: "0 auto", padding: "0 28px", height: "100%", boxSizing: "border-box" }}>
      <PageHeader
        title="게시판"
        subtitle={
          filterParam === "myposts" ? "내가 쓴 글" :
          filterParam === "mycomments" ? "내 댓글" :
          category ? `${cats.find(c => c.code === category)?.label ?? category} 게시판` : "전체 게시판"
        }
        right={
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <form onSubmit={e => { e.preventDefault(); setSearch(searchInput); setPage(1); }}
              style={{ display: "flex", gap: 6 }}>
              <input className="t-input" style={{ width: 180, height: 32, fontSize: 13 }}
                placeholder="제목 검색…" value={searchInput}
                onChange={e => setSearchInput(e.target.value)} />
              {search && (
                <button className="t-btn" type="button" style={{ height: 32, padding: "0 8px" }}
                  onClick={() => { setSearch(""); setSearchInput(""); setPage(1); }}>
                  <I.X size={12}/>
                </button>
              )}
            </form>
            <button className="t-btn t-btn-primary" style={{ display: "flex", gap: 6, alignItems: "center" }}
              onClick={() => setShowWrite(true)}>
              <I.Plus size={13}/> 글쓰기
            </button>
          </div>
        }
      />

      {/* 공지/게시판 탭 */}
      <BbsTabs active="board" />

      {/* 카테고리 필터 */}
      {cats.length > 0 && (
        <CatFilterBar cats={cats} active={category} onSelect={handleSelectCat} />
      )}

      {/* Main layout: table + detail panel */}
      <div style={{ display: "flex", gap: 0, height: "calc(100% - 180px)", minHeight: 400, overflow: "hidden" }}>
        {/* ── Table list ── */}
        <div style={{
          flex: 1, minWidth: 0, display: "flex", flexDirection: "column",
          background: "var(--surface)", border: "1px solid var(--border)",
          borderRadius: 12,
          overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
        }}>
          {/* Column headers */}
          <div style={{
            display: "grid", gridTemplateColumns: "104px 1fr 90px 76px 40px 52px",
            padding: "0 20px", height: 36,
            background: "rgba(0,0,0,0.025)", borderBottom: "2px solid var(--border)",
            alignItems: "center", flexShrink: 0,
          }}>
            {["분류","제목","작성자","날짜","💬","👁"].map((h, i) => (
              <span key={h} style={{
                fontSize: 11, fontWeight: 600, color: "var(--text-3)",
                textTransform: "uppercase", letterSpacing: "0.05em",
                textAlign: i >= 3 ? "center" : "left",
                paddingLeft: i === 1 ? 14 : 0,
              }}>{h}</span>
            ))}
          </div>

          {/* Rows */}
          <div style={{ flex: 1, overflowY: "auto" }}>
            {posts === null && (
              <div style={{ padding: "48px 0", textAlign: "center", color: "var(--text-3)", fontSize: 13 }}>
                불러오는 중…
              </div>
            )}
            {posts?.length === 0 && (
              <div style={{ padding: "64px 0", textAlign: "center", color: "var(--text-3)", fontSize: 14 }}>
                게시글이 없습니다
              </div>
            )}

            {pinned.map(p => (
              <PostRow key={p.id} post={p} cats={cats} active={false} onOpen={openPost} />
            ))}
            {pinned.length > 0 && normal.length > 0 && (
              <div style={{ borderBottom: "2px dashed var(--border)" }} />
            )}
            {normal.map(p => (
              <PostRow key={p.id} post={p} cats={cats} active={false} onOpen={openPost} />
            ))}
          </div>

          {/* Pagination */}
          {(posts?.length ?? 0) > 0 && (
            <div style={{
              display: "flex", gap: 8, justifyContent: "center", alignItems: "center",
              padding: "10px 16px", borderTop: "1px solid var(--border)", fontSize: 12, flexShrink: 0,
            }}>
              <button className="t-btn" style={{ padding: "3px 12px" }}
                disabled={page === 1} onClick={() => setPage(p => p - 1)}>← 이전</button>
              <span style={{ color: "var(--text-3)" }}>페이지 {page}</span>
              <button className="t-btn" style={{ padding: "3px 12px" }}
                disabled={(posts?.length ?? 0) < PAGE_SIZE} onClick={() => setPage(p => p + 1)}>다음 →</button>
            </div>
          )}
        </div>
      </div>

      {/* Write modal (new post) */}
      {showWrite && (
        <PostModal
          cats={cats}
          onClose={() => setShowWrite(false)}
          onSave={loadPosts}
        />
      )}
    </div>
  );
}
